/**
 * «Эволюция моей идеи» — лента снимков idea_card по ходу курса.
 *
 * Каждый снимок — карточка с:
 *   - заголовком вехи (Старт / После касдева / После юнит-эконом / После рекламы)
 *   - датой
 *   - значениями полей, с подсветкой того что изменилось vs предыдущий снимок
 *
 * Рендерится на home под «Карточкой моей идеи».
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../lib/api';
import type { IdeaCard, IdeaCardSnapshotItem, IdeaCardSnapshotsResponse } from '../types/cabinet';

const MILESTONE_LABEL: Record<string, string> = {
  start: 'Старт — как видел идею в начале',
  after_module_2: 'После касдева (Модуль 2)',
  after_module_4: 'После юнит-экономики (Модуль 4)',
  after_module_8: 'После плана запуска (Модуль 8)',
  // Веха прошлой программы из 12 модулей — для старых снимков.
  after_module_5: 'После юнит-экономики',
};

const MILESTONE_BADGE: Record<string, string> = {
  start: 'Старт',
  after_module_2: 'После М2',
  after_module_4: 'После М4',
  after_module_8: 'После М8',
  after_module_5: 'Экономика',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
  });
}

function pluralSnapshots(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'снимок';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'снимка';
  return 'снимков';
}

export function IdeaEvolutionTimeline() {
  const [collapsed, setCollapsed] = useState(true);

  const query = useQuery({
    queryKey: ['idea-card-history'],
    queryFn: async (): Promise<IdeaCardSnapshotsResponse> =>
      (await api.get<IdeaCardSnapshotsResponse>('/v1/cabinet/me/idea-card/history')).data,
  });

  const items = query.data?.items ?? [];

  // Не показываем секцию если ещё нет ни одного снимка (карточка не заполнена)
  // или есть только 1 снимок (нечего сравнивать — эволюции пока нет).
  if (items.length < 2) return null;

  return (
    <section className="mb-6 rounded-2xl bg-white dark:bg-slate-900 p-5 shadow-sm ring-1 ring-slate-200 dark:ring-slate-800 sm:p-6">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-brand-700 dark:text-brand-300">
            Эволюция моей идеи
          </p>
          <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">
            Как менялась моя идея — {items.length} {pluralSnapshots(items.length)}
          </h2>
        </div>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {collapsed ? 'показать' : 'свернуть'}
        </span>
      </button>

      {!collapsed && (
        <ol className="mt-5 space-y-4 border-l-2 border-slate-200 dark:border-slate-700 pl-5">
          {items.map((snap, i) => (
            <SnapshotCard
              key={snap.id}
              snap={snap}
              previous={i > 0 ? items[i - 1] : undefined}
              index={i}
              total={items.length}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function SnapshotCard({
  snap,
  previous,
  index,
  total,
}: {
  snap: IdeaCardSnapshotItem;
  previous: IdeaCardSnapshotItem | undefined;
  index: number;
  total: number;
}) {
  const milestone = snap.milestone ?? 'manual';
  const label = MILESTONE_LABEL[milestone] ?? 'Правка карточки';
  const badge = MILESTONE_BADGE[milestone] ?? '✎';
  const isLatest = index === total - 1;

  return (
    <li className="relative">
      <span
        className={`absolute -left-[27px] top-1 inline-flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white dark:ring-slate-900 ${
          isLatest ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-600'
        }`}
        aria-hidden
      />
      <div className="mb-1 flex flex-wrap items-baseline gap-2">
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
            isLatest
              ? 'bg-brand-100 dark:bg-brand-500/20 text-brand-800 dark:text-brand-200'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
          }`}
        >
          {badge}
        </span>
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{label}</p>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {formatDate(snap.created_at)}
        </span>
      </div>
      <SnapshotDiff current={snap.snapshot} previous={previous?.snapshot} />
    </li>
  );
}

// ---------------------------------------------------------------------------

const FIELDS: Array<{ key: keyof IdeaCard; label: string }> = [
  { key: 'niche', label: 'Ниша' },
  { key: 'audience', label: 'Кому' },
  { key: 'pain', label: 'Боль' },
  { key: 'target_revenue', label: 'Цель ₽/мес' },
  { key: 'deadline_days', label: 'Срок (дней)' },
];

function SnapshotDiff({
  current,
  previous,
}: {
  current: IdeaCard;
  previous: IdeaCard | undefined;
}) {
  return (
    <dl className="space-y-1.5 text-sm">
      {FIELDS.map(({ key, label }) => {
        const cur = current[key];
        const prev = previous?.[key];
        const changed = previous !== undefined && cur !== prev;
        const empty = cur === '' || cur === null || cur === undefined;
        if (empty && !changed) return null;
        return (
          <div key={key} className="flex flex-wrap gap-x-2 gap-y-0.5">
            <dt className="shrink-0 text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500 sm:w-24">
              {label}
            </dt>
            <dd className="flex-1">
              {changed && prev !== undefined && prev !== '' && prev !== null ? (
                <span className="block">
                  <span className="text-slate-500 dark:text-slate-400 line-through">
                    {formatValue(prev)}
                  </span>
                  <span className="ml-2 font-medium text-slate-900 dark:text-slate-100">
                    → {formatValue(cur)}
                  </span>
                </span>
              ) : (
                <span
                  className={
                    changed
                      ? 'font-medium text-emerald-700 dark:text-emerald-300'
                      : 'text-slate-700 dark:text-slate-300'
                  }
                >
                  {formatValue(cur)}
                  {changed && (
                    <span className="ml-1 text-emerald-600 dark:text-emerald-400">+</span>
                  )}
                </span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function formatValue(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'number') return v.toLocaleString('ru-RU');
  return v;
}
